import { useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTheme } from 'next-themes'
import {
  Building2, MessageSquare, Shield, Settings, LogOut, Sparkles, Sun, Moon,
} from 'lucide-react'
import { useAuthStore } from '../../stores/authStore'
import { useServerStore } from '@/shell/stores/serverStore'
import { useAppShellStore } from '../../stores/appShellStore'
import { useDmStore } from '@os/messenger/web/stores/dmStore'
import { useChannelStore } from '@/shell/stores/channelStore'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip'
import { APP_VERSION, MODULE_VERSIONS, BUILD_TIME } from '../../services/version'
import { mediaUrl, IS_CAPACITOR } from '../../services/config'
import { displayInitial } from '../../utils/displayInitial'
import { MODULE_CATALOG, isModuleEnabled, isRailModule } from '@os/contracts/shared'
import { MODULE_UI_META } from '../../utils/moduleUiMeta'
import { useActiveServer } from '../../hooks/useActiveServer'
import { useVisibleModules } from '../../hooks/useVisibleModules'
import { ServerSwitcher } from './ServerSwitcher'
import { useMandatoryLearningStore } from '../../stores/mandatoryLearningStore'
import { useTaskInboxStore } from '../../stores/taskInboxStore'
import { useLearningBadgeStore } from '../../stores/learningBadgeStore'
import { useHrBadgeStore } from '../../stores/hrBadgeStore'
import { useGuestStore } from '../../stores/guestStore'
import { useBrandStore } from '../../stores/brandStore'
import { BrandIcon } from '../brand/BrandMark'

// The rail is driven by MODULE_CATALOG @os/contracts) intersected with
// the active server's `enabledModules` subscription. Live vs Mock
// modules sit in two clusters separated by a dashed divider — the
// divider only renders if BOTH clusters have at least one visible
// entry so a fully-curated server (e.g. Rx2Go production with all
// mocks disabled) doesn't show a dangling line.
//
// Icons + Router URLs live in apps/web/src/utils/moduleUiMeta.ts to
// keep @os/contracts UI-framework-agnostic.

export default function PrimarySidebar({ onNavigate }: { onNavigate?: () => void } = {}) {
  const navigate = useNavigate()
  const location = useLocation()
  const { theme, setTheme } = useTheme()
  const { logout, user, updateUser } = useAuthStore()
  const { servers, activeServerId, setActiveServer } = useServerStore()
  const brandShortName = useBrandStore((s) => s.brand.shortName)
  const { section, setSection, lastBusinessPath, lastChatPath, aiPanelOpen, toggleAiPanel } = useAppShellStore()
  const inWorkspace = location.pathname.startsWith('/workspace')
  // Aggregate unread counters for the rail badges. Chats: sum of every
  // DM's unreadCount. Business OS: number of distinct text channels with
  // pending messages. We only render a badge when the count is > 0 so
  // the rail stays clean during the empty state.
  const dmChannels = useDmStore((s) => s.dmChannels)
  const unreadChannelIds = useChannelStore((s) => s.unreadChannelIds)
  const chatsUnread = dmChannels.reduce((sum, dm: any) => sum + (dm.unreadCount || 0), 0)
  const osUnread = unreadChannelIds.size

  const isAdmin = user?.isAdmin ?? false

  // OSPROD-160: an external-invite guest is scoped to a single channel,
  // so the rail shows ONLY the Spaces entry (which routes back to the
  // invited channel) — no Chats, modules, AI, Admin or Server Settings.
  const isGuest = useGuestStore((s) => s.isGuest)
  const goToGuestChannel = () => {
    const gs = useGuestStore.getState()
    if (gs.serverId && gs.channelId) navigate(`/servers/${gs.serverId}/channels/${gs.channelId}?guest=1`)
  }

  // Hard mandatory-learning gate: ANY user (admins INCLUDED) with incomplete
  // mandatory courses is locked to Learning, so the rail hides every other
  // section (Business OS, Chats, all modules except Learning, AI, Server
  // Settings, Admin). The route-level redirect lives in MandatoryLearningGate.
  const gated = useMandatoryLearningStore((s) => s.blocked)

  // Active server gates the rail's module filter AND the footer AI
  // Assistant toggle. The footer button uses the 'assistant' key; AI
  // Layer is the separate 'ai' rail module (rendered by the module loop
  // below). Resolution (incl. the non-Chat-route fallback chain) lives in
  // useActiveServer so AppLayout's AI-panel gate reads the exact same
  // server — turning the assistant off hides the footer toggle + panel.
  const activeServer = useActiveServer()
  const aiEnabled = isModuleEnabled(activeServer?.enabledModules, 'assistant')

  // Per-user module visibility (Phase B): the global admin can restrict a
  // module to specific departments / people. The endpoint returns the keys
  // THIS user may see; global admins get every enabled key, so their rail
  // is unchanged. null = loading / fail-open → no extra filtering applied.
  const visibleKeys = useVisibleModules(activeServer?.id)

  // Learning rail badge: number of courses the user hasn't completed yet
  // on the active server (mandatory + optional). Re-fetched on every
  // navigation (and server switch) so finishing a course clears it without
  // a reload — same cadence as the mandatory gate's poll. Fail-soft to 0
  // lives in the store, so the rail never breaks on a learning-API blip.
  const learningIncomplete = useLearningBadgeStore((s) => s.incompleteCount)
  // OSPROD-191: PERSONAL unread task-events count (assigned to me / reply on my
  // task / …) for the Tasks rail badge — NOT the board's unassigned-ticket count.
  // QA round 2: no longer cleared wholesale on opening the module — events clear
  // per board when the user opens that board (taskboards BoardView).
  const tasksUnread = useTaskInboxStore((s) => s.unread)
  const refreshLearningBadge = useLearningBadgeStore((s) => s.refresh)
  // HR rail badge: upcoming interviews (now..+7d) on vacancies the user can
  // see — same poll-on-nav cadence as the learning badge, fail-soft to 0.
  const hrUpcoming = useHrBadgeStore((s) => s.upcomingCount)
  const refreshHrBadge = useHrBadgeStore((s) => s.refresh)
  useEffect(() => {
    refreshLearningBadge(activeServer?.id)
    void refreshHrBadge()
  }, [activeServer?.id, location.pathname, refreshLearningBadge, refreshHrBadge])

  const handleServerSettings = () => {
    const targetId = activeServerId ?? servers[0]?.id
    if (targetId) {
      navigate(`/servers/${targetId}/admin`)
    }
  }

  const handleBusinessOS = () => {
    setSection('business')
    if (lastBusinessPath) {
      navigate(lastBusinessPath)
      return
    }
    const server = servers[0]
    if (server) {
      const ch = server.categories?.[0]?.channels?.[0] ?? server.channels?.[0]
      if (ch) navigate(`/servers/${server.id}/channels/${ch.id}`)
    }
  }

  const switchToServer = (id: string) => {
    setActiveServer(id)
    setSection('business')
    // Persist so reloads / other surfaces resolve the same active server
    // (same call Chat.tsx makes on server entry).
    updateUser({ settings: { lastActiveServerId: id } })
    const server = servers.find((s) => s.id === id)
    const ch = server?.categories?.[0]?.channels?.[0] ?? server?.channels?.[0]
    navigate(ch ? `/servers/${id}/channels/${ch.id}` : `/servers/${id}/admin`)
    onNavigate?.()
  }

  const handleChats = () => {
    setSection('chats')
    navigate(lastChatPath ?? '/channels/@me')
  }

  const handleDesktop = () => {
    setSection('desktop')
    navigate('/channels/@me')
  }

  const handleDashboards = () => {
    setSection('dashboards')
    navigate('/dashboards')
  }

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  const cycleTheme = () => {
    setTheme(theme === 'dark' ? 'light' : 'dark')
  }

  const ThemeIcon = theme === 'dark' ? Moon : Sun

  return (
    <TooltipProvider delayDuration={300}>
      <div className="w-14 shrink-0 flex flex-col items-center py-3 gap-1 bg-background border-r border-border">
        {/* Logo — opens the server switcher (OSPROD-199) when more than one
            server is reachable; otherwise the button's own onClick stands. */}
        <ServerSwitcher
          servers={gated || isGuest ? [] : servers}
          currentId={activeServerId}
          onSelect={switchToServer}
        >
          <button
            onClick={gated ? () => navigate('/workspace/learning') : isGuest ? goToGuestChannel : handleBusinessOS}
            className="w-10 h-10 rounded-lg flex items-center justify-center mb-3 shrink-0 overflow-hidden hover:bg-accent transition"
            title={brandShortName}
          >
            {/* White label: the rail mark comes from the tenant brand. Falls
                back to the stock logo asset on unbranded deploys and to an
                initials badge when a branded tenant uploaded no logo. */}
            <BrandIcon />
          </button>
        </ServerSwitcher>

        {/* Main navigation — hidden while the mandatory-learning gate is
            active so a blocked user can't escape to chat/business sections. */}
        {!gated && (
          <>
            <NavItem icon={<Building2 size={18} />} label={isGuest ? 'Spaces' : 'Business OS'} active={isGuest || (section === 'business' && !inWorkspace)} onClick={isGuest ? goToGuestChannel : handleBusinessOS} badge={isGuest ? undefined : osUnread} />
            {!isGuest && <NavItem icon={<MessageSquare size={18} />} label="Chats" active={section === 'chats' && !inWorkspace} onClick={handleChats} badge={chatsUnread} />}
          </>
        )}

        {/* Modules — filtered by the active server's `enabledModules`
            subscription. Live cluster (incl. Desktop with custom
            onClick) then mock cluster, both in catalog order. */}
        {(() => {
          const visible = MODULE_CATALOG.filter((m) =>
            // OSPROD-160: guests see no modules in the rail — only Spaces.
            !isGuest &&
            // Feature-toggle modules (rail:false, e.g. AI Assistant) are not
            // rail nav entries — they have their own footer/panel surface.
            isRailModule(m) &&
            // Trust and Device Management are managed organization
            // subscriptions and never inherit the legacy missing-key default.
            (m.key === 'trust' || m.key === 'mdm'
              ? activeServer?.enabledModules?.[m.key] === true
              : isModuleEnabled(activeServer?.enabledModules, m.key)) &&
            // Per-user visibility: hide modules the current user isn't
            // allowed to see (null = loading / fail-open → don't filter).
            (visibleKeys === null || visibleKeys.has(m.key)) &&
            // Gated users see only Learning in the rail.
            (!gated || m.key === 'learning'),
          )
          // Tracker is an admin/employer tool — it renders at the BOTTOM of the
          // rail (by the admin block), not in the top clusters. See the
          // dedicated block after the flex spacer below.
          const live = visible.filter((m) => m.status === 'live' && m.key !== 'tracker')
          const mock = visible.filter((m) => m.status === 'mock' && m.key !== 'tracker')
          const renderModule = (m: (typeof MODULE_CATALOG)[number], labelSuffix = '') => {
            const meta = MODULE_UI_META[m.key]
            const Icon = meta.icon
            // Desktop + Dashboards are section-switchers (set appShell section +
            // navigate to /channels/@me). All other modules just navigate.
            const isDesktop = m.key === 'desktop'
            const isDashboards = m.key === 'dashboards'
            const sectionActive =
              (isDesktop && section === 'desktop') || (isDashboards && section === 'dashboards')
            return (
              <NavItem
                key={m.key}
                icon={<Icon size={18} />}
                label={`${m.label}${labelSuffix}`}
                active={
                  isDesktop
                    ? sectionActive && !inWorkspace
                    : isDashboards
                      ? (sectionActive && !inWorkspace) || location.pathname.startsWith('/dashboards')
                      : location.pathname.startsWith(meta.match)
                }
                onClick={
                  isDesktop
                    ? handleDesktop
                    : isDashboards
                      ? handleDashboards
                      : () => navigate(meta.path)
                }
                badge={m.key === 'learning' ? learningIncomplete : m.key === 'tasks' ? tasksUnread : m.key === 'hr' ? hrUpcoming : undefined}
              />
            )
          }
          return (
            <>
              {live.map((m) => renderModule(m))}
              {mock.map((m) => renderModule(m, ' (mock)'))}
            </>
          )
        })()}

        <div className="flex-1" />

        {/* Tracker — admin/employer tool, positioned at the bottom by the admin
            block (not in the top module clusters). Visibility (admins-only by
            default via the 'admins' preset) is enforced by visibleKeys. */}
        {!isGuest && !gated && isModuleEnabled(activeServer?.enabledModules, 'tracker') &&
          (visibleKeys === null || visibleKeys.has('tracker')) && (() => {
            const meta = MODULE_UI_META['tracker']
            const Icon = meta.icon
            return (
              <NavItem
                icon={<Icon size={18} />}
                label="Tracker"
                active={location.pathname.startsWith(meta.match)}
                onClick={() => navigate(meta.path)}
              />
            )
          })()}

        {/* Footer actions */}
        {aiEnabled && !gated && !isGuest && (
          <NavItem icon={<Sparkles size={18} />} label="AI Assistant" active={aiPanelOpen} onClick={() => { toggleAiPanel(); onNavigate?.() }} />
        )}
        <NavItem icon={<ThemeIcon size={18} />} label={`Theme: ${theme}`} active={false} onClick={cycleTheme} />
        {/* Admin + Server Settings are web-only — hidden in the mobile (Capacitor)
            build, which ships no admin/billing/telephony UI. */}
        {!IS_CAPACITOR && isAdmin && !gated && !isGuest && <NavItem icon={<Shield size={18} />} label="Admin" active={false} onClick={() => navigate('/admin')} />}
        {!IS_CAPACITOR && !gated && !isGuest && <NavItem icon={<Settings size={18} />} label="Server Settings" active={false} onClick={handleServerSettings} />}

        {/* Profile avatar */}
        {user && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={() => navigate('/profile')}
                className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-xs font-bold mt-1 shrink-0 overflow-hidden hover:ring-2 hover:ring-primary/40 transition"
              >
                {user.avatarUrl ? (
                  <img src={mediaUrl(user.avatarUrl)} alt="" className="w-full h-full object-cover" />
                ) : (
                  displayInitial(user.username)
                )}
              </button>
            </TooltipTrigger>
            <TooltipContent side="right">
              <p>{user.username}</p>
            </TooltipContent>
          </Tooltip>
        )}

        {/* Divider before logout to prevent misclicks */}
        <div className="w-8 h-px bg-border my-2 shrink-0" />

        <NavItem icon={<LogOut size={18} />} label="Logout" active={false} danger onClick={handleLogout} />

        {/* Build label — sits at the very bottom of the rail so the
            user can confirm at a glance which build is shipped. The
            56-px rail is too narrow for "v0.0.1A" plus the prefix, so
            we drop the leading "v" and keep the chip tight; full text
            lives in the tooltip. */}
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              className="mt-1 select-none text-[9px] font-mono leading-none text-muted-foreground/70 px-1 py-0.5 rounded shrink-0 cursor-default"
              data-version={APP_VERSION}
            >
              {APP_VERSION}
            </span>
          </TooltipTrigger>
          <TooltipContent side="right">
            <div className="text-[11px] leading-snug">
              <div className="font-semibold">Build {APP_VERSION}</div>
              {BUILD_TIME && (
                <div className="opacity-60 mb-1.5">
                  built {new Date(BUILD_TIME).toLocaleString()}
                </div>
              )}
              {Object.keys(MODULE_VERSIONS).length > 0 && (
                <div className="space-y-0.5 font-mono pt-1 border-t border-current/15">
                  {Object.entries(MODULE_VERSIONS).map(([name, ver]) => (
                    <div key={name} className="flex items-center justify-between gap-4">
                      <span className="opacity-60">{name.replace('@os/', '')}</span>
                      <span>{ver}</span>
                    </div>
                  ))}
                </div>
              )}
              <a
                href="https://viewx.ai"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-1.5 pt-1.5 flex items-center justify-between gap-4 border-t border-current/15 font-semibold hover:underline"
              >
                <span className="text-primary">By ViewX OS</span>
                <span className="font-mono font-normal opacity-60">viewx.ai</span>
              </a>
            </div>
          </TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  )
}

interface NavItemProps {
  icon: React.ReactNode
  label: string
  active: boolean
  danger?: boolean
  onClick: () => void
  /** Numeric unread count; renders a small red dot/count when > 0. */
  badge?: number
}

function NavItem({ icon, label, active, danger, onClick, badge }: NavItemProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          onClick={onClick}
          aria-label={label}
          className={`relative w-10 h-10 rounded-lg flex items-center justify-center transition duration-150 ${
            active
              ? 'text-primary bg-primary/10'
              : danger
              ? 'text-muted-foreground hover:text-destructive hover:bg-destructive/10'
              : 'text-muted-foreground hover:text-foreground hover:bg-accent'
          }`}
        >
          {active && <span className="absolute left-0 top-1.5 bottom-1.5 w-0.5 bg-primary rounded-r-full" />}
          {icon}
          {badge !== undefined && badge > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold leading-none flex items-center justify-center ring-2 ring-background">
              {badge > 99 ? '99+' : badge}
            </span>
          )}
        </button>
      </TooltipTrigger>
      <TooltipContent side="right">
        <p>{label}</p>
      </TooltipContent>
    </Tooltip>
  )
}
